import {useTheme} from '@mui/material/styles';
import {useEffect, useMemo, useRef, useState} from 'react';

import {EMBEDDED_APPS} from './apps';
import {registerEmbeddedBottomChrome} from './bottomChrome';
import {
  EMBED_PROTOCOL_VERSION,
  EmbedContext,
  EmbedEnvelope,
  EmbedMessageType,
  InitPayload,
  isEmbedEnvelope,
  LayoutPayload,
  TokenPayload,
} from './protocol';

// Capabilities this host announces in `init` (see InitPayload.capabilities).
const HOST_CAPABILITIES = ['layout'];

// On the body while a sub-app is in fullscreen overlay mode. The title bar's
// floating actions (Ask AI / Feedback / Help) render at z-index 9999 — above
// the overlay's `theme.zIndex.modal` — so without this they'd pierce the
// overlay and sit on top of the sub-app's own controls; a global rule in
// theme.tsx hides them while the class is present.
export const EMBEDDED_APP_FULLSCREEN_CLASS = 'hasEmbeddedAppFullscreen';

interface EmbeddedAppProps {
  // Key into EMBEDDED_APPS.
  appId: string;
  // Current context to hand the sub-app; pushed live whenever it changes.
  context: EmbedContext;
  // Protocol capabilities announced in `init`; defaults to everything this host supports.
  capabilities?: string[];
}

// Host side of the embed protocol. Renders a sub-app in an iframe and:
//   - replies to its `ready` with `init` (context + freshly-minted token),
//   - pushes `context` whenever the `context` prop changes (live sync),
//   - answers `token:refresh` with a new token,
//   - obeys `layout` by expanding the iframe into a viewport overlay (and back),
//   - sends `teardown` on unmount.
// All postMessages are origin-checked against the app's registered origin.
export function EmbeddedApp({appId, context, capabilities = HOST_CAPABILITIES}: EmbeddedAppProps): JSX.Element | null {
  const app = EMBEDDED_APPS[appId];
  if (!app) throw new Error(`Unknown embedded app: ${appId}`);
  // Only operate when the app has a real, absolute URL configured for this
  // environment. Guards two things: `new URL('')` would throw at render, and an
  // empty `app.origin` would turn the postMessage origin check into a no-op.
  const configured = Boolean(app.url && app.origin);

  const theme = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Child-requested overlay mode ('layout' message). Style-only: the iframe
  // must never remount over this, or the sub-app would lose all its state.
  const [fullscreen, setFullscreen] = useState(false);
  // Latest context, read inside the (stable) message handler without resubscribing.
  const contextRef = useRef(context);
  contextRef.current = context;
  // Set once we've answered the child's `ready` with `init`; gates context
  // pushes so we never send `context` before the handshake completes.
  const initializedRef = useRef(false);

  const src = useMemo(() => {
    if (!configured) return '';
    if (app.buildSrc) return app.buildSrc(app.url, contextRef.current);
    const u = new URL(app.url);
    u.searchParams.set('embedded', '1');
    u.searchParams.set('customer', context.customer);
    u.searchParams.set('projectID', context.projectId);
    // The UI language, available to the sub-app from its very first render —
    // before the handshake delivers the context (e.g. its connecting screen).
    if (context.language) u.searchParams.set('lang', context.language);
    return u.toString();
    // Only seed the src once; live updates go over postMessage, not by reloading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.url, configured]);

  function postTo<T>(target: Window, type: EmbedMessageType, payload: T, reqId?: string): void {
    const msg: EmbedEnvelope<T> = {
      __maddoxEmbed: true,
      v: EMBED_PROTOCOL_VERSION,
      type,
      ...(reqId ? {reqId} : {}),
      payload,
    };
    target.postMessage(msg, app.origin);
  }

  useEffect(() => {
    if (!configured) return undefined;
    async function onMessage(event: MessageEvent): Promise<void> {
      // Trust only messages from the app's registered origin, and reply to the
      // exact window that sent them (not a captured ref, which can go stale when
      // the iframe remounts, e.g. under React StrictMode).
      if (!app.origin || event.origin !== app.origin) return;
      if (!isEmbedEnvelope(event.data)) return;
      const env = event.data;
      // Ignore messages from an incompatible protocol version (e.g. host/child
      // on different releases mid-deploy) before any token is minted.
      if (env.v !== EMBED_PROTOCOL_VERSION) return;
      const source = event.source as Window | null;
      if (!source) return;

      try {
        if (env.type === 'ready') {
          const {token, expiresAt} = await app.fetchToken();
          postTo<InitPayload>(source, 'init', {
            context: contextRef.current,
            token,
            expiresAt,
            capabilities,
          });
          initializedRef.current = true;
        } else if (env.type === 'token:refresh') {
          const {token, expiresAt} = await app.fetchToken();
          postTo<TokenPayload>(source, 'token', {token, expiresAt}, env.reqId);
        } else if (env.type === 'layout' && capabilities.includes('layout')) {
          setFullscreen(Boolean((env.payload as LayoutPayload).fullscreen));
        }
      } catch {
        // Token fetch failed/aborted; the child retries `ready`/`token:refresh`.
      }
    }

    window.addEventListener('message', onMessage);
    return () => {
      const target = iframeRef.current?.contentWindow;
      if (target) postTo(target, 'teardown', {});
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, configured]);

  // Hide the host chrome that would float above the overlay (see
  // EMBEDDED_APP_FULLSCREEN_CLASS). Cleanup runs on leaving fullscreen AND on
  // unmount, so navigating away mid-overlay can't strand the class on the body.
  useEffect(() => {
    if (!fullscreen) return undefined;
    document.body.classList.add(EMBEDDED_APP_FULLSCREEN_CLASS);
    return () => document.body.classList.remove(EMBEDDED_APP_FULLSCREEN_CLASS);
  }, [fullscreen]);

  useEffect(() => {
    if (!configured || !app.bottomChromeHeight) {
      return undefined;
    }
    return registerEmbeddedBottomChrome(appId, app.bottomChromeHeight);
  }, [appId, configured, app.bottomChromeHeight]);

  // Push context live whenever it changes — but only after the handshake has
  // completed (`init` already carries the current context, so an earlier push
  // would race it and reach the child before it's ready). Language included:
  // switching it in the user settings re-renders the sub-app's chrome in place.
  useEffect(() => {
    if (!initializedRef.current) return;
    const target = iframeRef.current?.contentWindow;
    if (target) postTo<EmbedContext>(target, 'context', context);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.customer, context.projectId, context.modelId, context.labelId, context.language, context.panelLayout]);

  if (!configured) return null;

  return (
    // In overlay mode only this wrapper's style changes; the iframe itself is
    // untouched so the sub-app keeps its state. `theme.zIndex.modal` puts the
    // overlay above the app chrome while the app's own dialogs still win.
    <div
      style={
        fullscreen
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: theme.zIndex.modal,
              backgroundColor: theme.palette.background.default,
            }
          : {width: '100%', height: '100%'}
      }
    >
      <iframe
        ref={iframeRef}
        src={src}
        title={appId}
        style={{width: '100%', height: '100%', border: 'none', display: 'block'}}
        // Same-origin (its own subdomain) so it can use its storage; scripts for the app.
        // Modals only for an app that opts in (EmbeddedAppConfig.allowModals).
        sandbox={`allow-scripts allow-same-origin allow-forms allow-popups${app.allowModals ? ' allow-modals' : ''}`}
      />
    </div>
  );
}
